import {
  EntityType,
  OrganizationService,
} from "@propertelligent/client-api";
import { useCallback, useRef, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import BluePayableIcon from "../../assets/Payment-Icon.svg";
import CustomTooltip from "../../components/common/CustomTooltip";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import SeparaterLine from "../../components/common/SeparaterLine";
import SimpleSpinner from "../../components/loader";
import ActionMenu from "../actionMenus/action-menu";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import EmptyState from "../emptystate";
import TruncatedDescriptionCell from "../common/TruncatedDescriptionCell";
import EditTransactionDrawer from "./EditTransactionDrawer";
import apiHelper from "../../utils/apiHelper";
import { GetImages } from "../../utils/GetImages";
import { usePermissions } from "../../hooks/usePermissions";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import { refundOrganizationTransaction } from "../../services/organizationTransaction.service";
import {
  canDeleteTransaction,
  isReversalTransaction,
  canRefundTransaction,
} from "./transactionActionUtils";
import type { TransactionRow } from "./transactionTypes";

interface FlatViewTableProps {
  payeeAccountData: any[];
  paymentMethodsArr: any[];
  rows: TransactionRow[];
  loading: boolean;
  loading1: boolean;
  hasMore: boolean;
  fetchData: (page: number) => void;
  currentPage: number;
  calculateValue?: Record<string, any>;
  aggregateValues?: Record<string, any>;
  formattedAggregateValues?: Record<string, any>;
  entityTypeId?: number;
  isContractorSelfView?: boolean;
  canManageTransactions?: boolean;
  hasActiveFilters?: boolean;
  // Notifies the detail page's own header (Past Due/Income Due) to refetch -- the Payments tab
  // already has this same wiring via PaymentTabContent's onRefresh prop; the Transactions tab
  // never had it, so deleting a transaction correctly reopened the linked due (confirmed via the
  // Payments tab) but the header stayed stale until a full reload (bug/ui-bugfix-batch8 item 5
  // follow-up). PubSub's RefreshLeaseData alone doesn't cover this -- its only subscriber is the
  // Tenants list page, not this detail page's own header.
  onRefresh?: () => void;
}

// Computed directly here rather than relying on CurrencyTooltip's own internal isNegative
// detection (which only inspects the raw "content" value for a leading minus/dash) -- live
// testing showed the "Raw" value passed to CurrencyTooltip for this aggregate doesn't reliably
// carry a sign, even though the paired "Formatted" string correctly wraps a negative amount in
// parentheses (bug/ui-bugfix-batch8 item 2, round 3). Checking the formatted string's own
// parentheses here means the "black" class is never even applied for a negative value in the
// first place, instead of depending on CurrencyTooltip to strip it off at render time.
const isNegativeAmount = (raw: unknown, formattedText?: string): boolean => {
  if (typeof raw === "number") return raw < 0;
  const rawStr = String(raw ?? "").trim();
  if (/^[-−]/.test(rawStr)) return true;
  const formattedStr = String(formattedText ?? "").trim();
  return formattedStr.includes("(") && formattedStr.includes(")");
};

const FlatViewTable = ({
  payeeAccountData,
  paymentMethodsArr,
  rows,
  loading,
  loading1,
  hasMore,
  fetchData,
  currentPage,
  calculateValue,
  formattedAggregateValues,
  entityTypeId,
  isContractorSelfView = false,
  canManageTransactions = false,
  onRefresh,
  hasActiveFilters = false,
}: FlatViewTableProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [transactionToEdit, setTransactionToEdit] =
    useState<TransactionRow | null>(null);
  const [transactionToDelete, setTransactionToDelete] =
    useState<TransactionRow | null>(null);
  const [transactionToRefund, setTransactionToRefund] =
    useState<TransactionRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const { canWrite } = usePermissions();
  const hasWritePermission = canWrite("Transaction");
  const showActions = canManageTransactions && hasWritePermission;
  const actionColumnSpan = showActions ? 10 : 9;

  const loadMoreRecords = useCallback(() => {
    if (!loading && hasMore) {
      fetchData(currentPage + 1);
    }
  }, [currentPage, fetchData, hasMore, loading]);

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMoreRecords,
    rootMargin: "0px 0px 100px 0px",
    // @ts-ignore
    root: scrollContainerRef.current,
  });

  const refreshTransactions = useCallback(() => {
    fetchData(1);
  }, [fetchData]);

  const publishFailure = useCallback((message: string) => {
    PubSub.publish(TopicsEnum.Notification.toString(), {
      type: NotificationType.Failure,
      message,
    });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!transactionToDelete?.id || isDeleting) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await apiHelper(
        OrganizationService.deleteApiOrganizationTransaction,
        {
          successMessage: "Successfully Deleted Transaction",
          showNotification: true,
        },
        transactionToDelete.id,
      );

      if (response?.data?.success) {
        setTransactionToDelete(null);
        refreshTransactions();
        // Deleting a transaction re-opens its linked PaymentDueAccount (backend already
        // reverses the paid amount) -- publish the same refresh topic pages/tenants/index.tsx's
        // RefreshLeaseData subscriber already consumes to refetch the Payments tab's header/row
        // amounts, so those update immediately instead of only after a manual page refresh
        // (bug/ui-bugfix-batch8 item 5).
        PubSub.publish(TopicsEnum.RefreshLeaseData.toString(), null);
        onRefresh?.();
        return;
      }

      if (!response?.statusCode && !response?.errors) {
        publishFailure("Failed to delete transaction. Try again.");
      }
    } catch (error) {
      publishFailure("Failed to delete transaction. Try again.");
    } finally {
      setIsDeleting(false);
    }
  }, [
    isDeleting,
    publishFailure,
    refreshTransactions,
    transactionToDelete,
    onRefresh,
  ]);

  const handleRefund = useCallback(async () => {
    if (!transactionToRefund?.id || isRefunding) {
      return;
    }

    setIsRefunding(true);
    try {
      const response = await apiHelper(
        refundOrganizationTransaction,
        {
          successMessage: "Successfully Refunded Transaction",
          showNotification: true,
        },
        transactionToRefund.id,
      );

      if (response?.data?.success) {
        setTransactionToRefund(null);
        refreshTransactions();
        return;
      }

      if (!response?.statusCode && !response?.errors) {
        publishFailure("Failed to refund transaction. Try again.");
      }
    } catch (error) {
      publishFailure("Failed to refund transaction. Try again.");
    } finally {
      setIsRefunding(false);
    }
  }, [
    isRefunding,
    publishFailure,
    refreshTransactions,
    transactionToRefund,
  ]);

  const buildMenuItems = useCallback(
    (transaction: TransactionRow) => {
      const items = [
        {
          icon: editIcon,
          label: "Edit",
          action: () => setTransactionToEdit(transaction),
          isDestructive: false,
        },
      ];

      if (canDeleteTransaction(transaction)) {
        items.push({ isDivider: true } as any);
        items.push({
          icon: deleteIcon,
          label: "Delete",
          action: () => setTransactionToDelete(transaction),
          isDestructive: true,
        } as any);
      }

      if (canRefundTransaction(transaction)) {
        items.push({ isDivider: true } as any);
        items.push({
          icon: BluePayableIcon,
          label: "Refund",
          action: () => setTransactionToRefund(transaction),
          isDestructive: false,
        } as any);
      }

      return items;
    },
    [],
  );

  const renderActions = (transaction: TransactionRow) => {
    if (!showActions) {
      return null;
    }

    if (isReversalTransaction(transaction)) {
      return <td className="normal_text" />;
    }

    return (
      <td className="normal_text">
        <ActionMenu
          Id={transaction.id}
          menuItems={buildMenuItems(transaction)}
          trigger={
            <button
              type="button"
              className="action-button iconStyleWithWhiteFilter"
              aria-label="More options"
            >
              <img src={dropdownIcon} alt="More options" />
            </button>
          }
        />
      </td>
    );
  };

  return (
    <div className="flat-view-border-container">
      {(loading || loading1) && rows.length === 0 ? (
        <div
          className="d-flex justify-content-center align-items-center flex-grow-1 w-100"
          style={{ minHeight: "60vh" }}
        >
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : (
        <>
          <div
            className="table-responsive"
            style={rows.length === 0 ? { overflowY: "hidden" } : undefined}
            ref={scrollContainerRef}
          >
            <table className="collapsible-table">
              <thead className="transaction-table-header">
                <tr>
                  <th>
                    <span>Description</span>
                  </th>
                  <th>
                    <span>From</span>
                  </th>
                  <th>
                    <span>To</span>
                  </th>
                  <th>
                    <span>Method</span>
                  </th>
                  <th>
                    <span>Reference</span>
                  </th>
                  <th>
                    <span>Category</span>
                  </th>
                  <th>
                    <span>Amount</span>
                  </th>
                  <th>
                    <span>Date</span>
                  </th>
                  <th>
                    <span>Transaction ID</span>
                  </th>
                  {showActions && (
                    <th>
                      <span>
                        <p>0</p>
                      </span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {!loading1 && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={actionColumnSpan}
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      <EmptyState
                        icon={GetImages.emptyStatePayment}
                        className="bg-transparent"
                        isBorder={false}
                        heading={hasActiveFilters ? "No Transaction Found With Applied Filters" : "No transaction found"}
                        isButton={false}
                      />
                    </td>
                  </tr>
                ) : (
                  rows.map((item, txIndex) => (
                    <tr
                      key={item.id ?? txIndex}
                      className="tooltip-main-row"
                      style={{ height: "unset" }}
                    >
                      <td>
                        <div className="normal_text" style={{ minWidth: 0 }}>
                          <span className="d-flex gap-1 align-items-center" style={{ minWidth: 0 }}>
                            <TruncatedDescriptionCell description={item?.description} maxChars={30} />
                          </span>
                        </div>
                      </td>
                      <td className="normal_text">
                        <CustomTooltip content={item?.paymentMethodAccountName} />
                      </td>
                      <td className="normal_text">
                        <CustomTooltip content={item?.payeeAccountName} />
                      </td>
                      <td className="normal_text">
                        <CustomTooltip
                          content={item?.formatted?.PaymentMethodTypeId}
                        />
                      </td>
                      <td className="normal_text">
                        <div className="compact-cell">
                          <CustomTooltip content={item?.reference}>
                            <span
                              className="d-inline-block text-truncate"
                              style={{ maxWidth: "100%" }}
                            >
                              {item?.reference}
                            </span>
                          </CustomTooltip>
                        </div>
                      </td>
                      <td className="normal_text">
                        <CustomTooltip
                          content={item?.formatted?.TranactionCreditDebitTypeId}
                        />
                      </td>
                      <td className="normal_text">
                        <CurrencyTooltip
                          content={item?.formatted?.AmountRaw}
                          formatted={item?.formatted?.Amount}
                        >
                          <span>{item?.formatted?.Amount}</span>
                        </CurrencyTooltip>
                      </td>
                      <td className="normal_text">
                        <CustomTooltip
                          content={item?.formatted?.PaymentDateDateOnly}
                        />
                      </td>
                      <td className="normal_text">
                        <div className="compact-cell">
                          <CustomTooltip content={item?.transactionId}>
                            <span
                              className="d-inline-block text-truncate"
                              style={{ maxWidth: "100%" }}
                            >
                              {item?.transactionId}
                            </span>
                          </CustomTooltip>
                        </div>
                      </td>
                      {renderActions(item)}
                    </tr>
                  ))
                )}

                {loading1 && rows.length > 0 && (
                  <tr className="flat-view-loader">
                    <td colSpan={actionColumnSpan}>
                      <SimpleSpinner myClassName="Icon40" />
                    </td>
                  </tr>
                )}

                {hasMore && (
                  <tr ref={sentryRef}>
                    <td
                      colSpan={actionColumnSpan}
                      style={{ height: "1px", padding: 0, border: "none" }}
                    />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading &&
        rows.length > 0 &&
        entityTypeId !== EntityType.Tenant &&
        !isContractorSelfView && (
          <div className="transaction-summary-footer">
            <div className="summary-left">
              <span className="normal_text black fw-500 lblSummary">
                Summary
              </span>
            </div>
            <div className="summary-right d-flex gap-2 justify-content-end">
              <span className="normal_text grey_subtext1 transaction-income">
                Income:
                <CurrencyTooltip
                  content={formattedAggregateValues?.TotalRevenueRaw}
                  formatted={formattedAggregateValues?.TotalRevenueFormatted}
                >
                  <span
                    className={`normal_text fw-600 ${
                      isNegativeAmount(
                        formattedAggregateValues?.TotalRevenueRaw,
                        formattedAggregateValues?.TotalRevenueFormatted,
                      )
                        ? "currency-negative"
                        : "black"
                    }`}
                  >
                    {formattedAggregateValues?.TotalRevenueFormatted || "NA"}
                  </span>
                </CurrencyTooltip>
              </span>

              <SeparaterLine height={"26px"} background={"#e5e7eb"} />

              <span className="normal_text grey_subtext1 transaction-income">
                Expenses:
                <CurrencyTooltip
                  content={formattedAggregateValues?.TotalExpenseRaw}
                  formatted={formattedAggregateValues?.TotalExpenseFormatted}
                >
                  <span
                    className={`normal_text fw-600 ${
                      isNegativeAmount(
                        formattedAggregateValues?.TotalExpenseRaw,
                        formattedAggregateValues?.TotalExpenseFormatted,
                      )
                        ? "currency-negative"
                        : "black"
                    }`}
                  >
                    {formattedAggregateValues?.TotalExpenseFormatted || "NA"}
                  </span>
                </CurrencyTooltip>
              </span>

              <SeparaterLine height={"26px"} background={"#e5e7eb"} />

              <span className="normal_text grey_subtext1 transaction-income">
                Net Income:
                {/* Color is computed directly here via isNegativeAmount (checking the formatted
                    string's own parentheses), not left to CurrencyTooltip's internal isNegative
                    detection -- the "Raw" value handed to CurrencyTooltip for this aggregate
                    doesn't reliably carry a sign even when the paired Formatted string correctly
                    shows parentheses, so "black" was never actually getting stripped for a
                    genuinely negative value (bug/ui-bugfix-batch8 item 2, round 3). */}
                <CurrencyTooltip
                  content={calculateValue?.NetProfitRaw}
                  formatted={calculateValue?.NetProfitFormatted}
                >
                  <span
                    className={`normal_text fw-600 ${
                      isNegativeAmount(calculateValue?.NetProfitRaw, calculateValue?.NetProfitFormatted)
                        ? "currency-negative"
                        : "black"
                    }`}
                  >
                    {calculateValue?.NetProfitFormatted || "NA"}
                  </span>
                </CurrencyTooltip>
              </span>
            </div>
          </div>
        )}

      <EditTransactionDrawer
        isOpen={!!transactionToEdit}
        transaction={transactionToEdit}
        paymentMethods={paymentMethodsArr}
        payeeAccounts={payeeAccountData}
        onClose={() => setTransactionToEdit(null)}
        onSaved={refreshTransactions}
      />

      <ConfirmationPopup
        isOpen={!!transactionToDelete}
        title="Delete Transaction"
        message="Do you want to delete this transaction?"
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDelete}
        onSecondaryAction={() => setTransactionToDelete(null)}
        onClose={() => setTransactionToDelete(null)}
        isPrimaryDestructive={true}
        isLoading={isDeleting}
        loadingLabel="Deleting..."
      />

      <ConfirmationPopup
        isOpen={!!transactionToRefund}
        title="Refund Transaction"
        message="Do you want to refund this transaction?"
        primaryButtonLabel="Refund"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleRefund}
        onSecondaryAction={() => setTransactionToRefund(null)}
        onClose={() => setTransactionToRefund(null)}
        isLoading={isRefunding}
        loadingLabel="Refunding..."
      />
    </div>
  );
};

export default FlatViewTable;
